/**
 * ChipInput — presentational + stateful chip/tag input (E10-F02-T04).
 *
 * Parity with the legacy CC input (`initCcInput` / `addCcChip` / `removeCcChip`)
 * and the Research-intake chip fields (competitors / DNS / targeting tags). One
 * component, two modes ({@link ChipMode}):
 *
 *   - `'email'`     — CC / address book: lowercase-normalise, regex-validate,
 *     reject invalid with an error toast ("Invalid email: …"), no duplicates.
 *   - `'free-text'` — tags / dictionaries: trim only, no email validation, no
 *     duplicates. Case preserved.
 *
 * Interaction (identical in both modes):
 *   - the input sits at the bottom; chips render above it (wrapping flex);
 *   - Enter OR comma commits the current value (trim + trailing-comma strip);
 *   - Backspace on an EMPTY input removes the last chip;
 *   - blur commits the current value;
 *   - clicking the container focuses the input (`onclick=…input.focus()` parity);
 *   - each chip is `bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full`
 *     with a `×` remove button (parity with `_renderCcChips`).
 *
 * State model — controlled by `value`/`onChange`. The pure decisions
 * (normalise / validate / dedup / key intent) live in {@link ./chip-input.js}.
 * CC auto-save (`update_project { cc_emails }` + local-state update) is NOT
 * baked in here: the consumer wires it through `onChange` (every chip add/remove
 * fires `onChange(nextChips)`), which keeps this atom feature-agnostic. See the
 * `CcChipInput` thin wrapper at the bottom for the CC-mode binding helper.
 *
 * Toasts: an invalid email raises an error toast via {@link useToast}. Outside a
 * ToastProvider that hook is a safe no-op, so a stray ChipInput never throws.
 */
import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useToast } from './ToastContext.js';
import {
  ChipRejectReason,
  chipKeyIntent,
  commitChipInput,
  removeChip,
  type ChipMode,
} from './chip-input.js';

export interface ChipInputProps {
  /** Current chips (controlled). */
  readonly value: readonly string[];
  /**
   * Called with the NEW chip array whenever a chip is added or removed. In
   * CC-mode the consumer persists this (`update_project { cc_emails }`) and
   * updates local state; in free-text mode it stores the tags on the form.
   */
  readonly onChange: (chips: string[]) => void;
  /** Value semantics. Default `'free-text'`. */
  readonly mode?: ChipMode;
  /** Placeholder for the entry input. */
  readonly placeholder?: string;
  /** Disable all interaction (chips render without remove buttons). */
  readonly disabled?: boolean;
  /** Accessible label for the entry input (defaults to the placeholder). */
  readonly ariaLabel?: string;
  /** Optional id for the entry input (for external label association). */
  readonly inputId?: string;
  /** Optional test id on the root container. */
  readonly testId?: string;
}

/** Shared chip pill classes — parity with legacy `_renderCcChips`. */
const CHIP_CLASS =
  'bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full flex items-center gap-1 max-w-xs';

/**
 * A single removable chip. The `×` button is hidden (not rendered) when
 * disabled so a read-only chip set is still legible.
 */
function Chip({
  label,
  onRemove,
  disabled,
}: {
  readonly label: string;
  readonly onRemove: () => void;
  readonly disabled: boolean;
}): ReactNode {
  return (
    <span className={CHIP_CLASS} data-testid="chip" data-chip-value={label}>
      <span className="truncate">{label}</span>
      {!disabled && (
        <button
          type="button"
          className="text-blue-500 hover:text-red-500 leading-none font-bold ml-0.5"
          // Don't let the click bubble to the container's focus handler.
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label={`Remove ${label}`}
          title="Remove"
        >
          {'×'}
        </button>
      )}
    </span>
  );
}

/**
 * Chip/tag input. Controlled via `value`/`onChange`; owns only the transient
 * text being typed in its entry field.
 */
export function ChipInput({
  value,
  onChange,
  mode = 'free-text',
  placeholder,
  disabled = false,
  ariaLabel,
  inputId,
  testId,
}: ChipInputProps): React.JSX.Element {
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const generatedId = useId();
  const resolvedInputId = inputId ?? generatedId;
  const toast = useToast();

  /** Try to commit the current text; on success append + clear the field. */
  const commit = (raw: string): void => {
    if (disabled) return;
    const result = commitChipInput(raw, value, mode);
    if (result.ok) {
      onChange([...value, result.value]);
      setText('');
      return;
    }
    if (result.reason === ChipRejectReason.Invalid && result.message) {
      // Parity: showNotification('Invalid email: …', 'error').
      toast.error(result.message);
      // Keep the bad text in the field so the user can fix it (legacy cleared
      // only on success; on blur the invalid value simply stays).
      return;
    }
    // Empty or duplicate → silently drop the entry text (no-op chip set).
    setText('');
  };

  const remove = (chip: string): void => {
    if (disabled) return;
    onChange(removeChip(value, chip));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (disabled) return;
    const intent = chipKeyIntent(e.key, text);
    if (intent === 'commit') {
      e.preventDefault();
      commit(text);
    } else if (intent === 'remove-last') {
      // Backspace on empty field: pop the last chip (parity).
      if (value.length > 0) {
        e.preventDefault();
        onChange(value.slice(0, -1));
      }
    }
  };

  return (
    <div
      data-testid={testId ?? 'chip-input'}
      className="flex flex-wrap gap-1 p-1.5 border rounded min-h-[34px] bg-white cursor-text"
      // Parity: clicking anywhere in the chip area focuses the entry input.
      onClick={() => inputRef.current?.focus()}
    >
      {value.map((chip) => (
        <Chip key={chip} label={chip} onRemove={() => remove(chip)} disabled={disabled} />
      ))}
      <input
        ref={inputRef}
        id={resolvedInputId}
        type="text"
        className="flex-1 min-w-[8rem] border-0 text-xs outline-none px-1.5 py-1 text-gray-700 bg-transparent"
        placeholder={placeholder}
        aria-label={ariaLabel ?? placeholder}
        disabled={disabled}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        // Parity: blur commits the current input value.
        onBlur={() => commit(text)}
        data-testid="chip-input-field"
      />
    </div>
  );
}

export interface CcChipInputProps {
  /** Current CC emails (controlled). */
  readonly value: readonly string[];
  /**
   * Persist + update local state. Wired by the consumer to
   * `update_project { cc_emails }` (auto-save on every add/remove) AND the
   * `state.currentProject.data.cc_emails` update — see legacy `_saveCcEmails`.
   */
  readonly onChange: (ccEmails: string[]) => void;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly inputId?: string;
  readonly testId?: string;
}

/**
 * CC-mode chip input: a thin wrapper that locks {@link ChipInput} into email
 * mode and supplies the legacy CC placeholder. The auto-save side effect
 * (update_project) is the consumer's `onChange` — keeping the persistence call
 * at the feature layer (it needs the project id + the API client), exactly as
 * the legacy `_saveCcEmails` lived next to `apiCall`.
 */
export function CcChipInput({
  value,
  onChange,
  placeholder = 'Add CC email, press Enter or comma',
  disabled,
  inputId,
  testId,
}: CcChipInputProps): React.JSX.Element {
  return (
    <ChipInput
      value={value}
      onChange={onChange}
      mode="email"
      placeholder={placeholder}
      ariaLabel="CC email"
      disabled={disabled}
      inputId={inputId}
      testId={testId ?? 'cc-chip-input'}
    />
  );
}
